import { checkImageLimits, checkSupportedFormats, confirm, deleteAssetImage, DropzonContent, Dropzone, FWKIcons, getAssetTypeLabel, handleRequestError, IAsset, IDropzoneLimits, Loading, Logger, toast, uploadAssetThumbnail } from "@desp-aas/desp-ui-fwk";
import { EImageState } from "@desp-aas/desp-ui-fwk/src/utils/storage";
import { Modal } from "@mui/material";
import axios from "axios";
import classNames from "classnames";
import { ChangeEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper, { Area, Point } from "react-easy-crop";
import AssetSource from "../../../components/AssetSource/AssetSource";
import { ASSET_IMAGE_ASPECT_RATIO } from "../../../utils/config";
import './AssetImage.css';


type IImageCrop = {
    zoom: number;
    area: Area;
}

type IProps = {
    asset: IAsset
    image?: string
    editMode?: boolean
    pageRef: RefObject<HTMLDivElement>
    imageHasBeenEdited: boolean
    setImageHasBeenEdited: (imageHasBeenEdited: boolean) => void
    imageCacheID: number
    setImageCacheID: (imageCacheID: number) => void
}

let timeout: any;

const logger = new Logger('Asset', 'AssetImage')

const IMAGE_FORMATS = [ "image/png", "image/jpeg", "image/jpg", "image/webp" ]

export const IMAGE_LIMITS: IDropzoneLimits = { dimensions: { height: 512, width: 512 } };


export const AssetImage = (props: IProps) =>{
    const ref = useRef<HTMLInputElement>(null);
    const [ newImage, setNewImage ] = useState<File|undefined>();
    const [ newImagePreview, setNewImagePreview ] = useState<string|ArrayBuffer|null|undefined>();
    const [ dragging, setDragging ] = useState(false);
    const [ updating, setUpdating ] = useState(false);
    const [ deleting, setDeleting ] = useState(false);
    const [ isDefaultImage, setIsDefaultImage ] = useState(true);
    const [ assetImage, setAssetImage ] = useState<string|undefined>();
    const [ croppingImage, setCroppingImage ] = useState<boolean>(false);
    const [ imageCrop, setImageCrop ] = useState<IImageCrop|undefined>();
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const cropCanvasRef = useRef<HTMLCanvasElement|null>(null);


    const onDragEnter = useCallback((ev: DragEvent)=>{ 
        setDragging(true); 
        if(timeout) clearTimeout(timeout)
        timeout = setTimeout(()=>{
            setDragging(false)
        }, 2000)
    }, [])

    useEffect(()=>{
        // console.log("props.pageRef.current", props.pageRef)
        // window.document.body.addEventListener("dragover", onDragEnter, true);
        props.pageRef.current?.addEventListener("dragover", onDragEnter, true);

        return ()=>{
            props.pageRef.current?.removeEventListener("dragover", onDragEnter, true);
        }

    }, [props.pageRef])
    
    const updatedImage = useCallback(()=>{
        props.setImageCacheID(Math.random())
    }, [])

    useEffect(()=>{
        if(!newImage) setNewImagePreview(undefined)
        else{
            const reader = new FileReader();
            reader.addEventListener("load", ()=>{
                setNewImagePreview(reader.result)
            })
            reader.readAsDataURL(newImage)
        }
    }, [ newImage ])

    const onEditClick = useCallback(()=>{
        if(ref.current && props.editMode){
            ref.current.click();
        }
    }, [ ref, props.editMode ])

    
    const onDrop = useCallback((files: FileList)=>{
        if(props.editMode){
            setDragging(false);
            setNewImage(files[0] ? files[0] : undefined)
            setCroppingImage(true);
        }
    }, [ props.editMode ])
    
    const handleInputChange = useCallback((ev: ChangeEvent<HTMLInputElement> )=>{
        if(!props.editMode || !(ev.target.files && ev.target.files.length > 0)) return;
        let file = ev.target.files?.[0];
        try{
            checkSupportedFormats(IMAGE_FORMATS, file);

            checkImageLimits( IMAGE_LIMITS, file)
            .then(()=>{
                setNewImage(file ? file : undefined)
                setCroppingImage(true);
            })
        }
        catch(e){}
    }, [ props.editMode ])

    const onConfirmClick = useCallback(()=>{
        if(props.editMode && newImage && imageCrop){
            setUpdating(true);
            uploadAssetThumbnail(
                props.asset.id.toString(), newImage,
                imageCrop.area.x, imageCrop.area.y, imageCrop.area.width, imageCrop.area.height
            )
            .then(()=>{
                toast(`Successfully updated ${getAssetTypeLabel(props.asset.type)} image`, { type: 'success' });
                setNewImage(undefined)
                setImageCrop(undefined);
                updatedImage()
            })
            .catch((e)=>{
                logger.error("Upload image error", e)
                handleRequestError(e, { defaultMessage: `Failed to update ${getAssetTypeLabel(props.asset.type)} image, please try again later` })
            })
            .finally(()=>{
                setUpdating(false);
            })
        }
    }, [ props.editMode, newImage, imageCrop ])

    const onCancelClick = useCallback(()=>{
        if(props.editMode){
            setNewImage(undefined)
        }
    }, [ props.editMode ])

    useEffect(()=>{
        if(!newImagePreview){
            setImageCrop(undefined);
        }
    }, [ newImagePreview ])

    useEffect(()=>{
        const canvas = cropCanvasRef.current;
        if(!canvas) return;

        if(!imageCrop || !newImagePreview){
            const ctx = canvas.getContext("2d");
            if(ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        if(newImagePreview){
            const img = new Image();
            img.onload = ()=>{
                const displayWidth = dropZoneRef.current?.clientWidth || 400;
                const displayHeight = dropZoneRef.current?.clientHeight || 260;

                canvas.width = displayWidth;
                canvas.height = displayHeight;

                const ctx = canvas.getContext("2d");
                if(ctx){
                    ctx.clearRect(0, 0, displayWidth, displayHeight);
                    ctx.drawImage(
                        img,
                        imageCrop.area.x, imageCrop.area.y,
                        imageCrop.area.width, imageCrop.area.height,
                        0, 0,
                        displayWidth, displayHeight
                    );
                }
            }
            img.src = newImagePreview as string;
        }
    }, [ cropCanvasRef, newImagePreview, imageCrop, dropZoneRef ])

    
    const onDeleteClick = useCallback(()=>{
        if(props.editMode){
            confirm({
                title: "Delete thumbnail",
                message: <>Are you sure you want to delete the thumbnail?</>,
                onConfirm: ()=>{
                    setDeleting(true)
                    deleteAssetImage(props.asset)
                    .then(()=>{
                        toast(<>Successfully removed asset image.</>, { type: 'success' })
                        updatedImage()
                    })
                    .catch((e)=>{
                        handleRequestError(e, { defaultMessage: "An error has occured during thumbnail deletion. Please try again later" })
                    })
                    .finally(()=>{
                        setDeleting(false)
                    })
                }
            })
        }
    }, [ props.editMode, props.asset ])

    useEffect(()=>{
        console.log("useEffect fetch image")
        axios.get(`/storage/thumbnail/${props.asset.id}`, {
            responseType: "blob",
            params: {
                cache: props.imageCacheID,
                edit_view: props.editMode ? "true" : undefined
            }
        })
        .then((response)=>{
            setIsDefaultImage(response.headers.is_default === "true")
            let contentType = response.headers["content-type"];
            if(contentType){
                let assetImage = URL.createObjectURL(response.data);
                props.setImageHasBeenEdited(response.headers.image_state?.toLowerCase() === EImageState.EDIT);
                setAssetImage(assetImage);
            }
        })
        .catch((e)=>{
            logger.error("get image error", e)
        })
    }, [ props.asset?.id, props.imageCacheID, props.editMode ]);

    const onAcceptClick = useCallback((zoom: number, area: Area)=>{
        setImageCrop({ zoom, area });
        setCroppingImage(false);
    }, [])
    
    const onCropImageClose = useCallback(()=>{
        setCroppingImage(false);
        if(!imageCrop){ 
            setNewImage(undefined);
        }
    }, [ imageCrop ])


    const loadingImage = useMemo(()=>{
        return updating || deleting;
    }, [ updating, deleting ])


    return (
        <Dropzone
            id="asset-banner-image"
            disableDrag={loadingImage || !props.editMode}
            limits={IMAGE_LIMITS}
            dropzoneContent={(dragging)=> dragging 
                ? <DropzonContent limits={IMAGE_LIMITS}>DROP NEW IMAGE HERE</DropzonContent>
                : <DropzonContent limits={IMAGE_LIMITS}>CLICK TO CHANGE</DropzonContent>
            }
            // dropzoneContent={(dragging)=> dragging ? "DROP NEW IMAGE HERE" : "CLICK TO CHANGE"  
            // }
            supportedFormats={IMAGE_FORMATS}
            onDrop={onDrop}
            dragRef={props.pageRef}
            dropzoneAreaClick={onEditClick}
            className={classNames({ "image": true, "dragging": dragging})}
            divProps={{
                style: { 
                    backgroundImage: `url(${assetImage})`,
                },
                ref: dropZoneRef
            }}
            pointerEvents
        >
            <canvas id="asset-image-crop-canvas" ref={cropCanvasRef} style={{ display: newImagePreview ? 'block' : 'none' }}/>
            <CroppingImageModal
                open={croppingImage}
                onClose={onCropImageClose}
                image={newImagePreview || assetImage}
                onAccept={onAcceptClick}
            />
            {
                props.asset &&
                <AssetSource asset={props.asset}/>
            }
            {
                props.editMode &&
                <>
                    <input 
                        ref={ref} id="asset-image-input" type="file" accept={IMAGE_FORMATS.join(",")}
                        onInput={handleInputChange} 
                        value={[]}
                    />
                    {
                        loadingImage &&
                        <div id="asset-banner-updating">
                            <Loading/>
                        </div>
                    }
                    <div id="asset-image-operations">
                        <div
                            className={classNames("button asset-image-edit operation blue", {"disabled": loadingImage})}
                            onClick={onEditClick}
                        >
                            { FWKIcons.edit } Edit
                        </div>
                        
                        {
                            newImage ?
                            <>
                                <div
                                    className={classNames({"button asset-image-accept blue operation": true, "disabled": loadingImage})}
                                    onClick={()=>{ setCroppingImage(true) }}
                                >
                                    { FWKIcons.crop } Resize
                                </div>
                                <div
                                    className={classNames({"button asset-image-accept green operation": true, "disabled": loadingImage})}
                                    onClick={onConfirmClick}
                                >
                                    { FWKIcons.confirm } Confirm
                                </div>
                                <div
                                    className={classNames({"button asset-image-accept red operation": true, "disabled": loadingImage})}
                                    onClick={onCancelClick}
                                >
                                    { FWKIcons.cancel } Cancel
                                </div>
                            </>
                            :
                            !isDefaultImage &&
                            <>
                            
                                <div
                                    className={classNames({"button asset-image-edit operation red": true, "disabled": loadingImage})}
                                    onClick={onDeleteClick}
                                >
                                    
                                    { deleting ? <Loading/> : FWKIcons.delete } Delete
                                </div>
                            </>
                        }

                        {
                            props.imageHasBeenEdited &&
                            <>
                                <div
                                    id="asset-image-updated"
                                    className={classNames({"button asset-image-edit operation": true, "disabled": loadingImage})}
                                >
                                    
                                    { FWKIcons.new }
                                    <span>validation</span>
                                    <span>required</span>
                                </div>
                            </>
                        }
                    </div>
                </>
            }

        </Dropzone>
    )
}


type ICroppingImageModalProps = {
    open: boolean;
    onClose: () => void;
    image: string | ArrayBuffer | null | undefined;
    onAccept: (zoom: number, area: Area) => void;
}

const DEFAULT_CROP = { x: 0, y: 0 };
const DEFAULT_ZOOM = 1;
const DEFAULT_AREA = { x: 0, y: 0, width: 0, height: 0 };

const CroppingImageModal = (props: ICroppingImageModalProps) => {

    const [ crop, setCrop ] = useState<Point>(DEFAULT_CROP);
    const [ zoom, setZoom ] = useState<number>(DEFAULT_ZOOM);
    const [ area, setArea ] = useState<Area>(DEFAULT_AREA);


    useEffect(()=>{
        setCrop(DEFAULT_CROP);
        setZoom(DEFAULT_ZOOM);
        setArea(DEFAULT_AREA);
    }, [ props.image ])

    const onAcceptClick = useCallback(()=>{
        props.onAccept(zoom, area);
    }, [ props.onAccept, zoom, area ])

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            id="cropping-image-modal"
        >
            <div id="cropping-image-content">
                <div id="cropping-image-cropper">
                    <Cropper
                        image={props.image as string}
                        crop={crop}
                        zoom={zoom}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        aspect={ASSET_IMAGE_ASPECT_RATIO}
                        zoomSpeed={0.2}
                        // aspect={16/9}
                        onCropComplete={(crop, croppedAreaPixels)=>{ setArea(croppedAreaPixels) }}
                    />
                </div>
                <div id="cropping-image-operations">
                    <button 
                        id="cropping-image-accept" 
                        className="button green operation medium"
                        onClick={onAcceptClick}
                    >
                        Accept
                    </button>
                    <button id="cropping-image-cancel" className="button red operation medium" onClick={props.onClose}>Cancel</button>
                </div>
            </div>
        </Modal>
    )
}